import { Component, OnChanges, OnInit, SimpleChanges, inject, ChangeDetectionStrategy, signal, input, output } from '@angular/core';
import {SureFlowLoopDetail} from '../../model/sure-flow-loop-detail';
import {SureFlowPipe} from '../../model/sure-flow-pipe';
import {RatingSureFlowService} from '../../services/rating-sure-flow.service';
import {StringUtils} from '../../utils/string-utils';
import {FluidType} from '../../model/fluid-type';
import {UnitSelectionService} from '../../services/unit-selection-service';
import {WorkingSureFlowUnit} from '../../model/working-sure-flow-unit';
import {RatingsModel} from '../../model/ratings-model';
import {SureFlowAutoSelectInput} from '../../model/sure-flow-auto-select-input';
import {DialogService} from '../../services/dialog.service';
import {LineChartData} from '../../model/chart-data/line-chart.data';
import {LineChartDataset} from '../../model/chart-data/line-chart-dataset';
import {UserStateService} from '../../services/user-state.service';
import {UnitConversionUtils} from '../../configurator/unit-conversion-utils';
import {MatProgressSpinner} from '@angular/material/progress-spinner';
import {LineChartComponent} from '../../charts/line-chart/line-chart.component';
import {SureFlowUnitsGridComponent} from '../sure-flow-units-grid/sure-flow-units-grid.component';
import {MatIcon} from '@angular/material/icon';
import {MatButton} from '@angular/material/button';
import {MatTooltip} from '@angular/material/tooltip';
import {FormsModule} from '@angular/forms';


@Component({
    selector: 'app-sureflow-loop-condition-detail',
    templateUrl: './sureflow-loop-condition-detail.component.html',
    styleUrls: ['./sureflow-loop-condition-detail.component.css'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [FormsModule, MatTooltip, MatButton, MatIcon, SureFlowUnitsGridComponent, LineChartComponent, MatProgressSpinner]
})
export class SureflowLoopConditionDetailComponent implements OnInit, OnChanges {
  private readonly ratingSureFlowService = inject(RatingSureFlowService);
  private readonly unitSelectionService = inject(UnitSelectionService);
  private readonly dialogService = inject(DialogService);
  private readonly userStateService = inject(UserStateService);


  readonly isCooling = input.required<boolean>();
  readonly detailInput = input.required<SureFlowLoopDetail>({ alias: 'detail' });
  readonly units = input.required<WorkingSureFlowUnit[]>();
  readonly invalidUnitIds = input.required<number[]>();
  readonly selectedUnitIds = input.required<number[]>();
  readonly addUnit = output<SureFlowLoopDetail>();
  readonly editUnits = output<void>();
  readonly deleteUnits = output<void>();
  readonly copyUnits = output<void>();
  readonly unitSelectionChanged = output<WorkingSureFlowUnit[]>();
  readonly addNonSureFlowUnit = output<SureFlowLoopDetail>();
  readonly recalculate = output<SureFlowLoopDetail>();
  readonly autoSelect = output<SureFlowAutoSelectInput>();
  readonly unitsRearranged = output<SureFlowLoopDetail>();
  readonly availablePipes = signal<SureFlowPipe[]>(undefined!);
  readonly isBusy = signal<boolean>(undefined!);
  readonly errorMessage = signal<string>(undefined!);
  warningMessage!: string;
  readonly allModels = signal<RatingsModel[]>(undefined!);
  coolingChartData!: LineChartData;
  heatingChartData!: LineChartData;
  selectedModelCode!: string;
  flowRateLocked!: boolean;
  flowRateInput!: number;
  temperatureChangeInput!: number;
  isInvalidFlowRate!: boolean;
  readonly loopFluidType = signal<FluidType>(undefined!);

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['selectedUnitIds']) {
      this.selectedModelCode = undefined!;
      if ((this.selectedUnitIds()?.length || 0) === 1) {
        this.selectedModelCode = this.units().filter(unit => this.selectedUnitIds()[0] === unit.id)[0]?.unitData?.sureFlowRatingUnit?.modelCode;
      }
    }
    if (changes['units']) {
      this.initializeChartData();
    }
    if (changes['detailInput']) {
      this.setDeltaValue();
      if (this.detailInput()) {
        if (this.detailInput().coolingLeavingFluidTemperature) {
          this.detailInput().coolingLeavingFluidTemperature = +this.detailInput().coolingLeavingFluidTemperature.toFixed(1);
        }
        if (this.detailInput().heatingLeavingFluidTemperature) {
          this.detailInput().heatingLeavingFluidTemperature = +this.detailInput().heatingLeavingFluidTemperature.toFixed(1);
        }
        if (this.detailInput().coolingActualTotalCapacity) {
          this.detailInput().coolingActualTotalCapacity = +this.detailInput().coolingActualTotalCapacity.toFixed(0);
        }
        if (this.detailInput().coolingActualSensibleCapacity) {
          this.detailInput().coolingActualSensibleCapacity = +this.detailInput().coolingActualSensibleCapacity.toFixed(0);
        }
        if (this.detailInput().heatingActualTotalCapacity) {
          this.detailInput().heatingActualTotalCapacity = +this.detailInput().heatingActualTotalCapacity.toFixed(0);
        }
      }
    }
  }

  private initializeChartData() {
    if (this.isCooling()) {
      this.initializeCoolingChartData();
    } else {
      this.initializeHeatingChartData();
    }
  }

  private initializeHeatingChartData(): void {
    this.heatingChartData = undefined!;
    if (this.units()) {
      const lineChartData = new LineChartData();
      lineChartData.labels = [];
      lineChartData.datasets = [];
      const targetTotalDataset = this.initializeDataset('Target Total Capacity', 'rgb(128,0,0)', 'rectRot', false);
      const actualTotalDataset = this.initializeDataset('Actual Total Capacity', 'rgb(128,0,0)', 'rectRot', true);
      for (const unit of this.units()) {
        lineChartData.labels.push(unit.unitData?.userTag || 'Unit ' + unit.sequence.toString());
        targetTotalDataset.data.push(unit.unitData?.heatingTargetTotalLoad || 0)
        if (unit.unitData?.sureFlowRatingUnit?.response) {
          const isSecondary = unit.unitData.sureFlowRatingUnit.response.secondaryCoilResponse;
          const actualCapacity = isSecondary ? unit.unitData.sureFlowRatingUnit.response.secondaryCoilResponse?.totalCapacity || 0
            : unit.unitData.sureFlowRatingUnit.response.primaryCoilResponse?.totalCapacity || 0;
          actualTotalDataset.data.push(actualCapacity);
        } else {
          actualTotalDataset.data.push(unit.unitData?.heatingTargetTotalLoad || 0)
        }
      }
      lineChartData.datasets.push(targetTotalDataset);
      lineChartData.datasets.push(actualTotalDataset);
      this.heatingChartData = lineChartData;
    }
  }

  private initializeCoolingChartData(): void {
    this.coolingChartData = undefined!;
    if (this.units()) {
      const lineChartData = new LineChartData();
      lineChartData.labels = [];
      lineChartData.datasets = [];
      const targetTotalDataset = this.initializeDataset('Target Total Capacity', 'rgb(0,24,129)', 'rectRot', false);
      const actualTotalDataset = this.initializeDataset('Actual Total Capacity', 'rgb(0,24,129)', 'rectRot', true);
      const targetSensibleDataset = this.initializeDataset('Target Sensible Capacity', 'rgb(0,103,89)', 'triangle', false);
      const actualSensibleDataset = this.initializeDataset('Actual Sensible Capacity', 'rgb(0,103,89)', 'triangle', true);
      const targetLatentDataset = this.initializeDataset('Target Latent Capacity', 'rgb(97,138,0)', 'rect', false);
      const actualLatentDataset = this.initializeDataset('Actual Latent Capacity', 'rgb(97,138,0)', 'rect', true);
      for (const unit of this.units()) {
        lineChartData.labels.push(unit.unitData?.userTag || 'Unit ' + unit.sequence.toString());
        const targetTotalLoad: number = unit.unitData?.coolingTargetTotalLoad || 0;
        let sensibleHeatRatio: number = this.detailInput()?.coolingTargetSensibleHeatRatio || 0;
        if (sensibleHeatRatio >= 1) {
          sensibleHeatRatio = sensibleHeatRatio / 100;
        }
        targetTotalDataset.data.push(targetTotalLoad);
        if (unit.unitData?.sureFlowRatingUnit?.response) {
          const totalCapacity = unit.unitData.sureFlowRatingUnit.response.primaryCoilResponse?.totalCapacity || 0;
          actualTotalDataset.data.push(totalCapacity);
          const targetSensibleLoad: number = unit.unitData?.coolingTargetSensibleLoad || (targetTotalLoad * sensibleHeatRatio);
          targetSensibleDataset.data.push(targetSensibleLoad);
          const sensibleCapacity = unit.unitData.sureFlowRatingUnit.response.primaryCoilResponse?.sensibleCapacity || 0;
          actualSensibleDataset.data.push(sensibleCapacity);
          targetLatentDataset.data.push(unit.unitData?.coolingTargetLatentLoad || targetTotalLoad - targetSensibleLoad)
          actualLatentDataset.data.push(totalCapacity - sensibleCapacity);
        } else {
          actualTotalDataset.data.push(unit.unitData?.coolingTargetTotalLoad || 0);
          targetSensibleDataset.data.push(unit.unitData?.coolingTargetSensibleLoad || 0)
          actualSensibleDataset.data.push(unit.unitData?.coolingTargetSensibleLoad || 0);
          targetLatentDataset.data.push(unit.unitData?.coolingTargetLatentLoad || 0)
          actualLatentDataset.data.push(unit.unitData?.coolingTargetLatentLoad || 0);
        }
      }
      lineChartData.datasets.push(targetTotalDataset);
      lineChartData.datasets.push(actualTotalDataset);
      lineChartData.datasets.push(targetSensibleDataset);
      lineChartData.datasets.push(actualSensibleDataset);
      lineChartData.datasets.push(targetLatentDataset);
      lineChartData.datasets.push(actualLatentDataset);
      this.coolingChartData = lineChartData;
    }
  }

  private initializeDataset(label: string, color: string, pointStyle: string, filled: boolean): LineChartDataset {
    const dataset: LineChartDataset = new LineChartDataset();
    const transparent: string = 'rgba(255, 255, 255, 0)';
    const white: string = 'rgb(255, 255, 255)';
    dataset.label = label;
    dataset.tension = .1;
    dataset.fill = false;
    dataset.borderColor = filled ? transparent : color;
    dataset.backgroundColor = transparent;
    dataset.pointStyle = pointStyle;
    dataset.pointBackgroundColor = filled ? color : white;
    dataset.pointBorderColor = color;
    dataset.pointBorderWidth = filled ? 3 : 2;
    dataset.pointRadius = filled ? 6 : 5;
    dataset.pointHoverRadius = dataset.pointBorderWidth + dataset.pointRadius + 4;
    dataset.data = [];
    return dataset;
  }

  private setDeltaValue() {
    this.warningMessage = undefined!;
    this.flowRateLocked = this.isCooling() ? this.detailInput().coolingPeakFluidFlowRateLocked : this.detailInput().heatingPeakFluidFlowRateLocked;
    const currentDelta = this.userStateService.getLoopDeltaValue(this.isCooling());
    if (this.isCooling()) {
      this.temperatureChangeInput = this.detailInput().coolingPeakFluidTemperatureChange;
      this.flowRateInput = this.detailInput().coolingPeakFluidFlowRate;
    } else {
      this.temperatureChangeInput = this.detailInput().heatingPeakFluidTemperatureChange;
      this.flowRateInput = this.detailInput().heatingPeakFluidFlowRate;
    }
    if (this.flowRateLocked && currentDelta < this.flowRateInput) {
      this.warningMessage = 'The loop flow rate has been updated to ' + this.flowRateInput + ' to support the requirements of the units on this loop';
      this.userStateService.setLoopDeltaValue(this.isCooling(), this.flowRateInput);
    }
    if ((this.availablePipes()?.length || 0) > 0) {
      if (this.isCooling() && this.detailInput().coolingPipeId) {
        const pipeId: number = +this.detailInput().coolingPipeId;
        const selectedPipe = this.availablePipes().filter(pipe => pipe.Id === pipeId)[0];
        const imperialFlowRate = this.detailInput().metric ? +UnitConversionUtils.convertFlowRate(this.detailInput().coolingPeakFluidFlowRate, false) : this.detailInput().coolingPeakFluidFlowRate;
        this.isInvalidFlowRate = imperialFlowRate > selectedPipe.MaxFlowRate;
      } else if (this.detailInput().heatingPipeId) {
        const pipeId: number = +this.detailInput().heatingPipeId;
        const selectedPipe = this.availablePipes().filter(pipe => pipe.Id === pipeId)[0];
        const imperialFlowRate = this.detailInput().metric ? +UnitConversionUtils.convertFlowRate(this.detailInput().heatingPeakFluidFlowRate, false) : this.detailInput().heatingPeakFluidFlowRate;
        this.isInvalidFlowRate = imperialFlowRate > selectedPipe.MaxFlowRate;
      }
    }
  }

  ngOnInit(): void {
    this.isBusy.set(true);
    this.unitSelectionService.getSureFlowModels().subscribe({
      next: (models) => {
        this.allModels.set(models);
        this.ratingSureFlowService.getSureFlowLoopPipes().subscribe({
          next: (pipes) => {
            this.availablePipes.set(pipes);
            this.unitSelectionService.getFluidTypes().subscribe({
              next: (fluidTypes) => {
                const fluidTypeCode = this.isCooling() ? this.detailInput().coolingFluidTypeCode : this.detailInput().heatingFluidTypeCode;
                this.loopFluidType.set(fluidTypes.filter(type => type.code === fluidTypeCode)[0]);
                this.isBusy.set(false);
              }, error: (error) => this.handleError(error)
            });
          }, error: (error) => this.handleError(error)
        });
      }, error: (error) => this.handleError(error)
    });
  }

  private handleError(error: any): void {
    this.isBusy.set(false);
    this.errorMessage.set(StringUtils.formatErrorMessage(error));
  }

  isGlycol(): boolean {
    return this.loopFluidType()?.glycol;
  }

  addLoopUnit(): void {
    this.addUnit.emit(this.detailInput());
  }

  unitSelected(units: WorkingSureFlowUnit[]): void {
    this.unitSelectionChanged.emit(units);
  }

  getModelImageFileName(): string {
    if ((this.selectedUnitIds()?.length || 0) === 1) {
      const selectedUnits = this.units().filter(unit => this.selectedUnitIds().indexOf(unit.id) >= 0);
      if ((selectedUnits[0].unitData?.sureFlowRatingUnit?.modelCode?.length || 0) > 0) {
        const selectedModelCode = selectedUnits[0].unitData.sureFlowRatingUnit.modelCode;
        const filtered = this.allModels().filter(model => model.modelCode === selectedModelCode);
        if (filtered.length === 1) {
          return filtered[0].imageFileName;
        }
      }
    }
    return '';
  }

  private getUnitSelectionCount(): number {
    return this.selectedUnitIds()?.length || 0;
  }

  canDelete(): boolean {
    return !this.isBusy() && this.getUnitSelectionCount() > 0;
  }

  deleteSelectedUnits(): void {
    this.deleteUnits.emit();
  }

  canCopy(): boolean {
    return !this.isBusy() && this.getUnitSelectionCount() > 0;
  }

  copySelectedUnit(): void {
    this.copyUnits.emit();
  }

  canEdit(): boolean {
    return !this.isBusy() && this.getUnitSelectionCount() > 0;
  }

  canRearrange(): boolean {
    return !this.isBusy() && (this.units()?.length || 0) > 1;
  }

  editSelectedUnit(): void {
    this.editUnits.emit();
  }

  addNonSFUnit(): void {
    this.addNonSureFlowUnit.emit(this.detailInput());
  }

  recalculateLoop(): void {
    const deltaValue = this.flowRateLocked ? this.flowRateInput : this.temperatureChangeInput;
    this.userStateService.setLoopDeltaValue(this.isCooling(), deltaValue);
    if (this.flowRateLocked) {
      if (this.isCooling()) {
        this.detailInput().coolingPeakFluidFlowRate = this.flowRateInput;
        this.detailInput().coolingPeakFluidTemperatureChange = undefined!;
        this.detailInput().coolingPeakFluidFlowRateLocked = this.flowRateLocked;
      } else {
        this.detailInput().heatingPeakFluidFlowRate = this.flowRateInput;
        this.detailInput().heatingPeakFluidTemperatureChange = undefined!;
        this.detailInput().heatingPeakFluidFlowRateLocked = this.flowRateLocked;
      }
    } else {
      if (this.isCooling()) {
        this.detailInput().coolingPeakFluidFlowRate = undefined!;
        this.detailInput().coolingPeakFluidTemperatureChange = this.temperatureChangeInput;
        this.detailInput().coolingPeakFluidFlowRateLocked = this.flowRateLocked;
      } else {
        this.detailInput().heatingPeakFluidFlowRate = undefined!;
        this.detailInput().heatingPeakFluidTemperatureChange = this.temperatureChangeInput;
        this.detailInput().heatingPeakFluidFlowRateLocked = this.flowRateLocked;
      }
    }
    this.recalculate.emit(this.detailInput());
  }

  autoSelectUnits(): void {
    const input = new SureFlowAutoSelectInput();
    input.cooling = this.isCooling();
    input.loopDetail = this.detailInput();
    if ((this.selectedUnitIds()?.length || 0) > 0) {
      input.selectedUnitIds = this.selectedUnitIds();
    } else {
      input.selectedUnitIds = [];
    }
    const allUnitIds = this.units().map(unit => unit.unitData.id);
    this.dialogService.showAutoSelectSureFlowUnitsDialog(input, allUnitIds).subscribe({
      next: (response) => {
        if (response) {
          this.autoSelect.emit(response);
        }
      }, error: (error) => this.handleError(error)
    });
  }

  rearrangeUnits(): void {
    if (this.canRearrange()) {
      this.unitsRearranged.emit(this.detailInput());
    }
  }

  temperatureInputChanged(): void {
    this.flowRateLocked = false;
    this.flowRateInput = undefined!;
    this.recalculateLoop();
  }

  flowRateInputChanged(): void {
    this.flowRateLocked = true;
    this.temperatureChangeInput = undefined!;
    this.recalculateLoop();
  }

}
